import React, { FC, Suspense } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import s from './App.scss';
import ErrorLayout from './ErrorLayout';
import BackgroundImage from './BackgroundImage';
import SymphonyCollage from './SymphonyCollage';
import SymphonyHeader from '../SymphonyHeader';
import dataHooks from '../constants/data-hooks';
import classNames from 'classnames';
import { BrandStyle } from '../constants';
import { useErrorData } from '../../hooks/use-error-data';
import { CUSTOM_PAGES } from '../constants/custom-pages';

interface AppProps extends WithTranslation {
  brandStyle: BrandStyle;
}

const App: FC<AppProps> = ({ brandStyle = 'classic' }) => {
  const isClassic = brandStyle === 'classic';
  const isNotBranded = brandStyle === 'notBranded';
  const isWixel = brandStyle === 'wixel';
  const isBase44 = brandStyle === 'base44';
  const isSymphony = brandStyle === 'symphony';

  const { errorCode } = useErrorData() || {};
  const baseCode = errorCode?.replace('-NotBranded', '');
  const CustomPage = baseCode ? CUSTOM_PAGES[baseCode] : undefined;

  if (CustomPage) {
    return (
      <Suspense fallback={null}>
        <CustomPage />
      </Suspense>
    );
  }

  return (
    <div
      className={classNames(s.root, {
        [s.wixelBackground]: isWixel,
        [s.base44Background]: isBase44,
        [s.symphonyBackground]: isSymphony,
      })}
      // The 404 frame is laid out on its own grid — further right, tighter gaps — where the
      // other codes share one set of values. An attribute carries it because the rules that
      // vary live in sibling stylesheets, which cannot see this file's scoped class names.
      data-error-code={baseCode}
      data-hook={dataHooks.root}
    >
      {isSymphony ? <SymphonyHeader /> : null}
      <div
        className={classNames(s.container, {
          [s.premiumContainer]: isNotBranded,
          [s.classicContainer]: !isNotBranded,
        })}
        data-hook={dataHooks.errorApp}
      >
        <ErrorLayout isNotBranded />
        {isClassic ? <BackgroundImage /> : null}
      </div>
      {isSymphony ? <SymphonyCollage /> : null}
    </div>
  );
};

export default withTranslation()(App);
